/**
 * Task V2 Repository - GraphQL Fragments
 *
 * Reusable GraphQL fragments for Task primitive.
 * Field parity with backend schema (Gate §44).
 *
 * @see graphql_gateway/src/task_api/task_model.rs - Backend schema
 */

import { gql } from '@apollo/client';

/** Core Task fields — matches GqlTask SimpleObject + ComplexObject resolvers */
export const TASK_FIELDS_FRAGMENT = gql`
  fragment TaskFields on Task {
    # Identity & Metadata
    id
    parentId
    rootTaskId
    name
    description
    taskType

    # Assignment & Ownership (DRR-117/DRR-118)
    assignedAt
    taskGroupRef
    mandateRef
    createdBySource
    formSchemaRef
    formComponents
    formSchemaOverride
    outputEntities {
      subgraph
      entityType
    }
    reassignmentAllowed
    reassignmentHistory {
      fromActorId
      toActorId
      reassignedAt
      reassignedBy
      reason
    }

    # Execution State
    status
    startedAt
    completedAt
    durationMs
    progressPercent
    currentStep
    errorMessage
    retryCount

    # Dependencies & Sequencing
    dependencies
    dependents
    sequenceOrder

    # Input/Output
    inputData
    outputData
    contextVariables
    expectedOutputSchema

    # Priority & Scheduling
    priority
    scheduledStart
    scheduledEnd

    # Retry & Error Handling
    maxRetries
    retryDelayMs
    failureStrategy

    # Lifecycle & Audit
    createdAt
    updatedAt
    version
    metadata

    # v1.1 Formula Integration Fields
    dueAt
    queuePosition
    notificationEvents
    currentActorWorkload
    estimatedDurationMs
    actualVsEstimatedRatio
    estimatedCost
    actualCost

    # Append-only architecture
    isActive

    # Computed fields — resolved from constellation edges (DRR-118)
    supervisorActorId
    authorityActorId
    auditorActorId

    # Computed fields — DRR-117 mandate jurisdiction
    mandateJurisdiction
    mandateIsOverride
    mandateSourceUrl

    # Computed fields — DRR-130 violation
    violationOutcome
    violationReviewDeadline

    # Compressed ID (lazy-loaded)
    compressedId

    # DRR-124: Actor summary resolvers
    assignedActor {
      id
      displayName
    }
    teamActor {
      id
      displayName
    }
    assignedByActor {
      id
      displayName
    }
    subjectActor {
      id
      displayName
    }
    createdByActor {
      id
      displayName
    }
    updatedByActor {
      id
      displayName
    }
  }
`;

/** Task connection fields for pagination */
export const TASK_CONNECTION_FRAGMENT = gql`
  fragment TaskConnectionFields on TaskConnection {
    items {
      ...TaskFields
    }
    total
    nextCursor
    hasMore
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Task statistics fields */
export const TASK_STATISTICS_FRAGMENT = gql`
  fragment TaskStatisticsFields on TaskStatistics {
    tenantId
    totalTasks
    pendingCount
    inProgressCount
    completedCount
    failedCount
    avgDurationMs
    avgProgressPercent
    slaBreachedCount
    slaApproachingCount
  }
`;
